import React, { useEffect, useRef } from "react";
import { useForm, FieldError } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Button from "../common/Button";
import Dropdown from "../common/Dropdown";
import {
  templateCreateSchema,
  templateUpdateSchema,
  TemplateFormData,
  TemplateUpdateFormData,
} from "../../schemas/templateSchema";
import { useTemplateCategoryOptions } from "../../hooks/useTemplateCategoryOptions";

const typeOptions = [
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
];

interface TemplateFormProps {
  isOpen: boolean;
  initialData?: TemplateFormData | TemplateUpdateFormData;
  onSubmit: (data: TemplateFormData | TemplateUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isUpdate?: boolean;
  existingZipFileName?: string;
  existingPreviewFileName?: string;
}

const TemplateForm: React.FC<TemplateFormProps> = ({
  isOpen,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isUpdate = false,
  existingZipFileName,
  existingPreviewFileName,
}) => {
  const { templateCategoryOptions, isTemplateCategoryOptionsLoading } =
    useTemplateCategoryOptions();

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    trigger,
    clearErrors,
  } = useForm<TemplateFormData | TemplateUpdateFormData>({
    resolver: zodResolver(
      isUpdate ? templateUpdateSchema : templateCreateSchema
    ),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      type: undefined,
      description: "",
      categoryId: "",
    },
  });

  const prevIsOpenRef = useRef(false);
  const zipFileInputRef = useRef<HTMLInputElement | null>(null);
  const previewFileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset form only when modal opens (not on every render)
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      if (initialData) {
        reset({
          name: initialData.name || "",
          type: (initialData.type as "image" | "video") || undefined,
          description: initialData.description || "",
          categoryId: initialData.categoryId || "",
        });
        // Reset file inputs manually
        if (zipFileInputRef.current) {
          zipFileInputRef.current.value = "";
        }
        if (previewFileInputRef.current) {
          previewFileInputRef.current.value = "";
        }
      } else {
        reset({
          name: "",
          type: undefined,
          description: "",
          categoryId: "",
        });
        // Reset file inputs manually
        if (zipFileInputRef.current) {
          zipFileInputRef.current.value = "";
        }
        if (previewFileInputRef.current) {
          previewFileInputRef.current.value = "";
        }
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialData, reset]);

  const handleFormSubmit = (
    data: TemplateFormData | TemplateUpdateFormData
  ) => {
    onSubmit(data);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={initialData ? "Update Template" : "Create Template"}
      size="md"
    >
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="space-y-6"
        noValidate
      >
        <Input
          label="Name"
          placeholder="Enter template name"
          name="name"
          value={watch("name") || ""}
          onChange={(e) => setValue("name", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("name")}
          error={errors.name as FieldError | undefined}
        />

        <Dropdown
          label="Type"
          options={typeOptions}
          value={watch("type")}
          name="type"
          onChange={(val) => setValue("type", val as "image" | "video", { shouldValidate: false })}
          onBlur={() => trigger("type")}
          error={errors.type as FieldError | undefined}
        />

        <Input
          label="Description"
          placeholder="Optional"
          name="description"
          value={watch("description") || ""}
          onChange={(e) => setValue("description", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("description")}
          error={errors.description as FieldError | undefined}
        />

        <Dropdown
          label="Category"
          options={templateCategoryOptions || []}
          value={watch("categoryId")}
          name="categoryId"
          onChange={(val) => setValue("categoryId", val as string, { shouldValidate: false })}
          onBlur={() => trigger("categoryId")}
          error={errors.categoryId as FieldError | undefined}
          disabled={isTemplateCategoryOptionsLoading}
        />

        <div>
          <Input
            label="Zip File"
            name="zipFile"
            type="file"
            onChange={(e) => {
              // Manually set the FileList to ensure React Hook Form captures it correctly
              if (e.target.files && e.target.files.length > 0) {
                setValue("zipFile", e.target.files as any, { shouldValidate: false });
                // Clear error immediately when file is selected
                clearErrors("zipFile");
              } else {
                // Clear the value if no file is selected
                setValue("zipFile", undefined as any, { shouldValidate: false });
              }
            }}
            onBlur={() => {
              // Don't trigger file validation on blur — clicking the file input
              // opens a native dialog which fires blur immediately before any
              // file is selected, causing a premature error.
            }}
            error={errors.zipFile as any}
            accept=".zip"
            ref={zipFileInputRef}
          />
          {isUpdate && existingZipFileName && (
            <div className="mt-2 p-3 bg-gray-100 rounded border border-gray-300">
              <p className="text-sm text-gray-600 font-medium">
                Current file: {existingZipFileName}
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Leave empty to keep existing file
              </p>
            </div>
          )}
          {isUpdate && !existingZipFileName && (
            <p className="text-gray-500 text-sm mt-1">
              Leave empty to keep existing file
            </p>
          )}
        </div>

        <div>
          <Input
            label="Preview File"
            name="previewFile"
            type="file"
            onChange={(e) => {
              // Manually set the FileList to ensure React Hook Form captures it correctly
              if (e.target.files && e.target.files.length > 0) {
                setValue("previewFile", e.target.files as any, { shouldValidate: false });
                // Clear error immediately when file is selected
                clearErrors("previewFile");
              } else {
                // Clear the value if no file is selected
                setValue("previewFile", undefined as any, { shouldValidate: false });
              }
            }}
            onBlur={() => {
              // Don't trigger file validation on blur — clicking the file input
              // opens a native dialog which fires blur immediately before any
              // file is selected, causing a premature error.
            }}
            error={errors.previewFile as any}
            accept="image/*"
            ref={previewFileInputRef}
          />
          {isUpdate && existingPreviewFileName && (
            <div className="mt-2 p-3 bg-gray-100 rounded border border-gray-300">
              <p className="text-sm text-gray-600 font-medium">
                Current file: {existingPreviewFileName}
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Leave empty to keep existing file
              </p>
            </div>
          )}
          {isUpdate && !existingPreviewFileName && (
            <p className="text-gray-500 text-sm mt-1">
              Leave empty to keep existing file
            </p>
          )}
        </div>

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading}>
            {initialData ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default TemplateForm;
